import {
  importTypes,
  settingStepFieldKeys,
  settingStepFields,
  validators,
} from '../config/import';
import {
  getImportDataRows,
  getWorksheetLastRow,
  parseImportSheet,
} from '../lib/file';
import { getSettingStepRequiredFieldSize, stateKeys } from './config';
import { uploadingFieldNames } from '../config/uploadingStep';
import { stepKeys } from '../config/step';
import { normalize } from '../lib/transformValues';

const {
  uploadingValues: uploadingValuesKey,
  uploadingErrors: uploadingErrorsKey,
  workbook: wbKey,
  workbookSheetData: wbSheetDataKey,
  isFileUploading: isFileUploadingKey,
  settingValues: settingValuesKey,
  settingErrors: settingErrorsKey,
  workbookValidationResult: wbValidationKey,
  previewData: previewKey,
  step: stepKey,
} = stateKeys;

const {
  house: houseName,
  accountType: accountTypeName,
  importType: importTypeName,
  file: fileName,
  sheet: sheetName,
  dataStartRow: dataStartRowName,
} = uploadingFieldNames;

const resetSettingStepData = (state) => {
  const { [settingValuesKey]: values, [settingErrorsKey]: errors } = state;

  if (values.size) {
    state[settingValuesKey] = new Map();
  }

  if (errors.size) {
    state[settingErrorsKey] = new Map();
  }
};

const resetPreviewData = (state) => {
  const { [wbValidationKey]: validationResult, [previewKey]: previewData } =
    state;

  if (validationResult) {
    state[wbValidationKey] = undefined;
  }

  if (previewData) {
    state[previewKey] = undefined;
  }
};

const resetSettingAndPreviewData = (state) => {
  resetSettingStepData(state);
  resetPreviewData(state);
};

const getSheetDataSize = (obj) => (obj ? Object.keys(obj).length : 0);
const sheetDataErrorHandler = ({ state, importType }) => {
  const sheetData = state[wbSheetDataKey];

  if (!Array.isArray(sheetData)) {
    return state;
  }

  const type = importType || state[uploadingValuesKey].get(importTypeName);
  const uploadingErrors = state[uploadingErrorsKey];
  const fields = settingStepFields[type];
  const wb = state[wbKey];
  const key = wb.SheetNames.length === 1 ? fileName : sheetName;

  if (sheetData.length === 0) {
    uploadingErrors.set(key, [`Пустой лист`]);
    state[uploadingErrorsKey] = new Map(uploadingErrors);
    return state;
  }

  const fieldsSize = getSettingStepRequiredFieldSize(fields);
  const sheetDataSize = getSheetDataSize(sheetData[0]);

  if (sheetDataSize < fieldsSize) {
    uploadingErrors.set(key, [
      `Колонок с данными - ${sheetDataSize}. Должно быть минимум ${fieldsSize}`,
    ]);
    state[uploadingErrorsKey] = new Map(uploadingErrors);
    return state;
  }

  if (uploadingErrors.has(key)) {
    uploadingErrors.delete(key);
    state[uploadingErrorsKey] = new Map(uploadingErrors);
    return state;
  }

  return state;
};

const setErrAndRemoveVal = ({
  state,
  name,
  msgs,
  errKey = uploadingErrorsKey,
  valKey = uploadingValuesKey,
}) => {
  const { [errKey]: errors, [valKey]: values } = state;

  errors.set(name, msgs);
  state[errKey] = new Map(errors);

  if (values.has(name)) {
    values.delete(name);
    state[valKey] = new Map(values);
  }

  return state;
};

const setFieldValue = ({
  state,
  name,
  value,
  errKey = uploadingErrorsKey,
  valKey = uploadingValuesKey,
}) => {
  const { [errKey]: errors, [valKey]: values } = state;

  values.set(name, value);
  state[valKey] = new Map(values);

  if (errors.has(name)) {
    errors.delete(name);
    state[errKey] = new Map(errors);
  }

  return state;
};

const removeUploadingFields = (state, names) => {
  const values = state[uploadingValuesKey];
  const errors = state[uploadingErrorsKey];

  names.forEach((name) => {
    values.delete(name);
    errors.delete(name);
  });

  state[uploadingValuesKey] = new Map(values);
  state[uploadingErrorsKey] = new Map(errors);
};

const getCurrentWorksheet = (state) => {
  const workbook = state[wbKey];
  if (!workbook) return undefined;

  const selectedSheetName =
    state[uploadingValuesKey].get(sheetName) || workbook.SheetNames[0];

  return workbook.Sheets[selectedSheetName];
};

const updateSheetData = ({ state, worksheet = getCurrentWorksheet(state) }) => {
  if (!worksheet) {
    state[wbSheetDataKey] = undefined;
    return state;
  }

  const uploadingValues = state[uploadingValuesKey];
  const dataStartRow = uploadingValues.get(dataStartRowName);
  const importType = uploadingValues.get(importTypeName);

  if (importType === importTypes.personalAccountsByCompanies && !dataStartRow) {
    state[wbSheetDataKey] = undefined;
    return state;
  }

  state[wbSheetDataKey] = parseImportSheet({ worksheet, dataStartRow });
  return state;
};

export const houseChangeHandler = ({ state, value }) => {
  resetPreviewData(state);

  if (!value) {
    return setErrAndRemoveVal({
      state,
      name: houseName,
      msgs: ['Выберите дом'],
    });
  }

  return setFieldValue({ state, name: houseName, value });
};

export const accountTypeChangeHandler = ({ state, value }) => {
  resetPreviewData(state);

  if (!value) {
    return setErrAndRemoveVal({
      state,
      name: accountTypeName,
      msgs: ['Выберите тип ЛС'],
    });
  }

  return setFieldValue({ state, name: accountTypeName, value });
};

export const importTypeChangeHandler = ({ state, value }) => {
  resetSettingAndPreviewData(state);

  const isCompanyAccountsImport =
    value === importTypes.personalAccountsByCompanies;
  const scopeFieldsToRemove = isCompanyAccountsImport
    ? [houseName]
    : value
    ? [accountTypeName, dataStartRowName]
    : [houseName, accountTypeName, dataStartRowName];
  removeUploadingFields(state, scopeFieldsToRemove);

  const updatedState = !value
    ? setErrAndRemoveVal({
        state,
        name: importTypeName,
        msgs: ['Укажите, что загружать'],
      })
    : setFieldValue({ state, name: importTypeName, value });

  if (updatedState[wbKey]) {
    updateSheetData({ state: updatedState });
  }

  return sheetDataErrorHandler({ state: updatedState, importType: value });
};

const uploadFileErrorHandler = ({ state, errors }) => {
  const updatedState = setErrAndRemoveVal({
    state,
    name: fileName,
    msgs: errors,
  });

  updatedState[wbKey] = undefined;
  updatedState[wbSheetDataKey] = undefined;

  const {
    [uploadingValuesKey]: uploadingValues,
    [uploadingErrorsKey]: uploadingErrors,
  } = updatedState;

  if (uploadingValues.has(sheetName)) {
    uploadingValues.delete(sheetName);
    updatedState[uploadingValuesKey] = new Map(uploadingValues);
  }

  if (uploadingErrors.has(sheetName)) {
    uploadingErrors.delete(sheetName);
    updatedState[uploadingErrorsKey] = new Map(uploadingErrors);
  }

  return updatedState;
};

const multipleSheetsHandler = (state) => {
  const {
    [uploadingValuesKey]: uploadingValues,
    [uploadingErrorsKey]: uploadingErrors,
    [wbKey]: wb,
  } = state;

  const { SheetNames: sheetNames, Sheets: sheets } = wb;

  const currentSheetName = uploadingValues.get(sheetName);

  if (sheetNames.includes(currentSheetName)) {
    updateSheetData({ state, worksheet: sheets[currentSheetName] });
  } else {
    uploadingValues.delete(sheetName);
    state[uploadingValuesKey] = new Map(uploadingValues);
    state[wbSheetDataKey] = undefined;
  }

  if (uploadingErrors.has(sheetName)) {
    uploadingErrors.delete(sheetName);
    state[uploadingErrorsKey] = new Map(uploadingErrors);
  }
};

const singleSheetHandler = (state) => {
  const {
    [uploadingValuesKey]: uploadingValues,
    [uploadingErrorsKey]: uploadingErrors,
    [wbKey]: wb,
  } = state;

  const { SheetNames: sheetNames, Sheets: sheets } = wb;

  const currentSheetName = sheetNames[0];
  uploadingValues.set(sheetName, currentSheetName);
  state[uploadingValuesKey] = new Map(uploadingValues);

  if (uploadingErrors.has(sheetName)) {
    uploadingErrors.delete(sheetName);
    state[uploadingErrorsKey] = new Map(uploadingErrors);
  }

  updateSheetData({ state, worksheet: sheets[currentSheetName] });
};

export const fileChangeHandler = ({ state, payload }) => {
  resetSettingAndPreviewData(state);
  removeUploadingFields(state, [dataStartRowName]);

  const { value, errors, workbook } = payload;

  state[isFileUploadingKey] = false;

  if (errors?.length) {
    return uploadFileErrorHandler({ state, errors });
  }

  const updatedState = setFieldValue({ state, name: fileName, value });
  updatedState[wbKey] = workbook;

  const { SheetNames: sheetNames } = workbook;

  if (sheetNames.length > 1) {
    multipleSheetsHandler(updatedState);
  } else if (sheetNames.length === 1) {
    singleSheetHandler(updatedState);
  }

  return sheetDataErrorHandler({ state: updatedState });
};

export const sheetChangeHandler = ({ state, value }) => {
  resetSettingAndPreviewData(state);
  removeUploadingFields(state, [dataStartRowName]);

  if (!value) {
    const updatedState = setErrAndRemoveVal({
      state,
      name: sheetName,
      msgs: ['Укажите страницу файла'],
    });

    updatedState[wbSheetDataKey] = undefined;
    return updatedState;
  }

  const updatedState = setFieldValue({ state, name: sheetName, value });

  const sheets = updatedState[wbKey].Sheets;
  updateSheetData({ state: updatedState, worksheet: sheets[value] });

  return sheetDataErrorHandler({ state: updatedState });
};

export const dataStartRowChangeHandler = ({ state, value }) => {
  resetSettingAndPreviewData(state);

  const isEmpty = value === undefined || value === null || value === '';

  if (isEmpty) {
    const updatedState = setErrAndRemoveVal({
      state,
      name: dataStartRowName,
      msgs: ['Укажите строку начала данных'],
    });

    updateSheetData({ state: updatedState });
    return updatedState;
  }

  const rowNumber = Number(value);
  const worksheet = getCurrentWorksheet(state);
  const lastRow = getWorksheetLastRow(worksheet);

  if (!Number.isInteger(rowNumber) || rowNumber < 1 || rowNumber > lastRow) {
    const updatedState = setErrAndRemoveVal({
      state,
      name: dataStartRowName,
      msgs: [`Укажите номер строки от 1 до ${lastRow}`],
    });

    updateSheetData({ state: updatedState });
    return sheetDataErrorHandler({ state: updatedState });
  }

  const updatedState = setFieldValue({
    state,
    name: dataStartRowName,
    value: rowNumber,
  });

  updateSheetData({ state: updatedState, worksheet });
  return sheetDataErrorHandler({ state: updatedState });
};

export const settingStepFieldChangeHandler = ({ state, name, value }) => {
  resetPreviewData(state);

  const keys = { errKey: settingErrorsKey, valKey: settingValuesKey };

  return !value
    ? setErrAndRemoveVal({
        ...keys,
        state,
        name,
        msgs: ['Выберите соответствие'],
      })
    : setFieldValue({
        ...keys,
        state,
        name,
        value,
      });
};

const errorsToStr = (errors) => errors.map((err) => err.message).join('; ');

export const goToPreviewHandler = ({ state, payload }) => {
  const validationResult = state[wbValidationKey];

  state[stepKey] = stepKeys.preview;

  if (!validationResult) {
    const {
      [uploadingValuesKey]: uploadingValues,
      [settingValuesKey]: settingValues,
      [wbSheetDataKey]: rawSheetData,
    } = state;

    const importType = uploadingValues.get(importTypeName);
    const validator = validators[importType](payload);
    const dataStartRow = uploadingValues.get(dataStartRowName);
    const sheetData = getImportDataRows({
      sheetData: rawSheetData,
      dataStartRow,
    });

    state[wbValidationKey] = sheetData.reduce(
      (acc, row) => {
        const item = {};
        let hasValues = false;

        const keys = Object.values(settingStepFieldKeys[importType]);

        keys.forEach((key) => {
          const value = settingValues.get(key);
          const rowValue = normalize(row[value]);

          if (rowValue && !hasValues) {
            hasValues = true;
          }

          item[key] = rowValue;
        });

        if (hasValues) {
          validator.validate(item, { suppressWarning: true }, (err) => {
            const target = err ? acc.invalid : acc.valid;
            const result = err ? { ...item, errors: errorsToStr(err) } : item;
            target.push(result);
          });
        }

        return acc;
      },
      {
        valid: [],
        invalid: [],
      }
    );
  }

  return state;
};
